/* eslint-disable */
import * as React from "react";
import * as Highcharts from "highcharts";
import HighchartsMore from "highcharts/highcharts-more";
import variwideHighcharts from "highcharts/modules/variwide";
import HighchartsReact from "highcharts-react-official";
import { WithStyles, withStyles } from "@material-ui/styles";
import { Theme } from "@material-ui/core";
import * as merge from "lodash.merge";
import { cloneDeep, isNull } from 'lodash';
import sunburst from "highcharts/modules/sunburst.js";
import Treemap from "highcharts/modules/treemap";
import styles from "./styles";
import { ClassNameMap } from "@material-ui/styles/withStyles";
import ReactResizeDetector from "react-resize-detector";
import cn from "classnames";

HighchartsMore(Highcharts);
Treemap(Highcharts);
variwideHighcharts(Highcharts);

type HighchartsChartClassKey = "root" | "chart" | "legend";
export type Chartpoint = Highcharts.Point & { attributes: Record<string, string> }

type OwnProps = {
  data: any;
  includeMore?: boolean;
  immutable?: boolean;
  customClasses?: Partial<ClassNameMap<HighchartsChartClassKey>>;
  useResizeDetector?: boolean;
  disabledSeries?: string[];
  onHover?: (point: Chartpoint | undefined) => void;
  loader?: any;
  theme: Theme;
};
type Props = OwnProps & WithStyles<typeof styles>;

const HighchartsChart: React.FC<Props> = (props) => {
  const isExport = !!(window as any).applyDefinition;
  const debounceRenderTimer = React.useRef<any>();
  const [dataOptions, setDataOptions] = React.useState<any>();
  const { data, theme, classes, customClasses, onHover } = props;
  const { useResizeDetector = true, immutable = true, loader = null } = props;
  const chartRef = React.useRef<Highcharts.Chart | null>();

  React.useEffect(() => {
    clearTimeout(debounceRenderTimer.current);

    debounceRenderTimer.current = setTimeout(() => {
      setDataOptions(data);
    }, 500);

    return () => {
      chartRef.current = null;
      setDataOptions(null);
    }
  }, [JSON.stringify(data)]);


  const setChartRef = React.useCallback((chartInstance: Highcharts.Chart) => {
    if (!isExport) {
      chartRef.current = chartInstance;
    }
  }, [chartRef]);

  const chart = React.useMemo(() => {
    if (!dataOptions) {
      return loader;
    }
    const handleResize = (width: number, height: number) => {
      if (chartRef.current) {
        const appliedWidth = chartRef.current['showLegend'] ? width - 290 : width;
        const appliedHeight = height;
        if (chartRef.current['chartId']) {
          chartRef.current['width'] = appliedWidth;
          chartRef.current['height'] = appliedHeight;
        } else {
          chartRef.current.setSize(appliedWidth, appliedHeight, false);
        }
      }
    };
    const exportOptions = isExport ? {
      zoomPanelOptions: {
        enabled: false,
      },
      legend: {
        ...dataOptions.legend,
        y: 0,
      },
    } : {};

    const getAxisStyles = (dataAxis, axisStylesTemplate) => {
      const axisStyles: any = {};
      if (!dataAxis || dataAxis.length === 0) {
        return cloneDeep(axisStylesTemplate);
      }
      if (!Array.isArray(dataAxis)) {
        return merge(dataAxis, cloneDeep(axisStylesTemplate));
      }

      for (let axisIndex = 0; axisIndex < dataAxis.length; axisIndex++) {
        if (axisIndex !== 0 && !dataAxis[axisIndex]) {
          continue;
        }
        axisStyles[axisIndex] = merge(axisStyles[axisIndex], cloneDeep(axisStylesTemplate));
      }
      return axisStyles;
    };
    const xAxisStyles = {
      gridLineColor: theme.palette.action.selected,
      lineColor: theme.palette.text.hint,
      tickColor: theme.palette.text.hint,
      labels: {
        style: {
          color: "#666666",
          fontWeight: "bold",
          fontFamily: ['Lato', 'sans-serif'].join(','),
        }
      },
      title: {
        style: { color: theme.palette.text.primary }
      },
    };
    const yAxisStyles = {
      ...xAxisStyles,
    }
    const basfTheme = {
      colors: ['#0F8AC0', '#1BB8C2', '#1CA955', '#E38114', '#DD5C3D', '#8F8F8F', '#D9D10C', '#2D5EDA', '#784FBA', '#D656A3'],
      chart: {
        backgroundColor: theme.palette.background.paper
      },
      title: undefined,
      plotOptions: {
        series: {
          animation: false,
          dataLabels: {
            borderWidth: 0,
            shadow: false,
            style: {
              textOutline: '0',
              color: 'black'
            }
          },
          point: {
            events: {
              mouseOver: function () {
                if (onHover) {
                  onHover(this as any)
                }
              },
              mouseOut: function () {
                if (onHover) {
                  onHover(undefined)
                }
              }
            }
          }
        },
        "treemap": {
          dataLabels: {
            borderWidth: undefined,
            shadow: true,
            style: {
              textOutline: undefined
            }
          }
        }
      },
      credits: {
        enabled: false,
      },
      subtitle: {
        style: { color: theme.palette.text.hint }
      },
      tooltip: {
        backgroundColor: theme.palette.action.selected,
        useHTML: true,
        style: {
          fontFamily: ['Lato', 'sans-serif'].join(','),
          color: theme.palette.text.primary
        }
      },
      legend: {
        itemHiddenStyle: { color: theme.palette.action.disabled },
        itemHoverStyle: { color: theme.palette.text.primary },
        itemStyle: {
          color: theme.palette.text.hint,
          fontFamily: ['Lato', 'sans-serif'].join(',')
        }
      },
      xAxis: dataOptions && getAxisStyles(dataOptions.xAxis, xAxisStyles),
      yAxis: dataOptions && getAxisStyles(dataOptions.yAxis, yAxisStyles),
    };
    const chartOptions = cloneDeep(merge({}, basfTheme, dataOptions, exportOptions));

    return (<div className={cn(classes.root, customClasses && customClasses.root)}>
      {useResizeDetector && (
        <ReactResizeDetector handleWidth={true} handleHeight={true} onResize={handleResize} />
      )}
      <div className={cn(classes.chartContainer, customClasses && customClasses.chart)}>
        <div className={classes.highChartsContainer}>
          <HighchartsReact
            highcharts={Highcharts}
            options={chartOptions}
            containerProps={{ className: classes.highChartsContainer }}
            immutable={immutable}
            callback={setChartRef}
          />
        </div>
      </div>
    </div>)
  }, [dataOptions]);

  return chart;
}

export default withStyles(styles, { withTheme: true })(HighchartsChart);
